import { forwardRef } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/WarningAmber';
import { SnackbarContent, Stack, type SnackbarContentProps } from '@mui/material';
import { useCloseNotification } from 'react-admin';
import { IconButton } from 'shared/mui/IconButtons';

const NOTIFICATION_TYPES_WITH_ICONS = ['success', 'error', 'warning'] as const;

type NotificationTypeWithIcon = (typeof NOTIFICATION_TYPES_WITH_ICONS)[number];

const icons: Record<NotificationTypeWithIcon, React.ReactElement> = {
  success: <CheckCircleIcon color="success" fontSize="small" />,
  error: <ErrorIcon color="error" fontSize="small" />,
  warning: <WarningIcon color="warning" fontSize="small" />,
};

const getNotificationType = (className?: string) =>
  NOTIFICATION_TYPES_WITH_ICONS.find((type) => className?.includes(`RaNotification-${type}`)) ??
  'info';

export const NotificationContent = forwardRef<HTMLDivElement, SnackbarContentProps>(
  ({ className, message, ...props }, ref) => {
    const closeNotification = useCloseNotification();

    const type = getNotificationType(className);

    return (
      <SnackbarContent
        {...props}
        action={
          <IconButton onClick={closeNotification} size="small" sx={{ padding: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        className={className}
        message={
          <Stack alignItems="center" direction="row" gap={1}>
            {type !== 'info' && icons[type]}
            <span>{message}</span>
          </Stack>
        }
        ref={ref}
      />
    );
  },
);

NotificationContent.displayName = 'NotificationContent';
